import { useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { Button, Card, IconButton, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { useCanWrite } from '@/access/useCanWrite';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { PageTitle } from '@/components/shell/PageTitle';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { applicationStatusTone } from './domain';
import { ApplicationEditDialog } from './ApplicationEditDialog';
import { ApplicationEgressIpsPanel } from './ApplicationEgressIpsPanel';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Application detail against `GET`/`PATCH /v1/admin/applications/{code}`
 * (A3's contract). There is no `notifySecret` anywhere on this screen's read
 * path — `AdminApplication` never carries it — only the edit dialog accepts
 * a fresh value to write.
 */
export function ApplicationDetailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const canWrite = useCanWrite();
  const applicationCode = code ?? '';
  const [editOpen, setEditOpen] = useState(false);
  const { data, loading, error, reload } = useApiResource(
    () => payApiClient.getAdminApplication(applicationCode),
    applicationCode,
  );

  const backButton = (
    <IconButton onClick={() => navigate('/applications')} aria-label={translate('pay.applications.detail.back')} size="small">
      <ArrowBackOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          <LoadingState variant="card" />
        </Card>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          {error.errorCode === 'access_denied' ? (
            <AccessDenied />
          ) : error.errorCode === 'not_found' ? (
            <EmptyState
              title={translate('pay.applications.detail.notFoundTitle')}
              body={translate('pay.applications.detail.notFoundBody')}
            />
          ) : (
            <ErrorState body={error.message} onRetry={reload} />
          )}
        </Card>
      </Stack>
    );
  }

  if (!data) return null;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {backButton}
        <PageTitle
          title={data.name}
          subtitle={data.code}
          adornment={<StatusChip label={translate(`pay.applications.status.${data.status}`)} tone={applicationStatusTone(data.status)} />}
        />
      </Stack>

      <SectionHeader onRefresh={reload} />

      <Card sx={{ p: 2.5, maxWidth: 560 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {translate('pay.applications.detail.fieldStatus')}
            </Typography>
            <StatusChip label={translate(`pay.applications.status.${data.status}`)} tone={applicationStatusTone(data.status)} />
          </Stack>
          <Field
            label={translate('pay.applications.detail.fieldNotifyUrl')}
            value={data.notifyUrl ?? translate('pay.applications.detail.notifyUrlEmpty')}
          />
          <Field label={translate('pay.applications.detail.fieldCreatedAt')} value={new Date(data.createdAt).toLocaleString('es-ES')} />
          {canWrite && (
            <Stack direction="row" sx={{ pt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => setEditOpen(true)}>
                {translate('pay.applications.detail.editAction')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>

      <ApplicationEgressIpsPanel applicationCode={data.code} />

      <ApplicationEditDialog
        open={editOpen}
        application={data}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          reload();
        }}
      />
    </Stack>
  );
}
